import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  FolderOpen, BookOpen, ShieldCheck, FileText, Hospital, Heart,
  Download, Eye, CalendarDays, Search, File, Award, Play,
  Gift, Bus, HeartHandshake, Pill, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { documentiPaziente, materialiEducativi, serviziTerritorio } from '@/data/mockPatientData';

/* ══════ CONFIG ══════ */
const tabs = [
  { id: 'documenti', label: 'I miei Documenti', icon: FolderOpen },
  { id: 'educazione', label: 'Impara e Scopri', icon: BookOpen },
];

const tipoConfig = {
  'lettera-dimissione': { icon: Hospital, color: '#EF4444', bg: '#FEF2F2', label: 'Lettera Dimissione' },
  referto: { icon: FileText, color: '#3B82F6', bg: '#EFF6FF', label: 'Referto' },
  prescrizione: { icon: File, color: '#8B5CF6', bg: '#F5F3FF', label: 'Prescrizione' },
  pai: { icon: Heart, color: '#10B981', bg: '#ECFDF5', label: 'Piano Cure' },
  certificato: { icon: Award, color: '#F59E0B', bg: '#FFFBEB', label: 'Certificato' },
};

const tipoEduConfig = {
  video: { icon: Play, color: '#EF4444', bg: '#FEF2F2', label: 'Video' },
  guida: { icon: FileText, color: '#3B82F6', bg: '#EFF6FF', label: 'Guida PDF' },
};

const servizioIcons = { bonus: Gift, trasporto: Bus, psicologia: HeartHandshake, farmacia: Pill };
const servizioColors = { bonus: '#F59E0B', trasporto: '#3B82F6', psicologia: '#8B5CF6', farmacia: '#10B981' };

/* ══════ DOC VIEWER MODAL ══════ */
function DocViewer({ doc, onClose }) {
  if (!doc) return null;
  const cfg = tipoConfig[doc.tipo] || tipoConfig.referto;
  const Icon = cfg.icon;
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-foreground/30 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="doc-viewer-overlay"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
        className="bg-card rounded-3xl shadow-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="doc-viewer-panel"
      >
        <div className="p-7 space-y-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: cfg.bg }}>
              <Icon className="w-6 h-6" style={{ color: cfg.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-lg font-bold text-foreground">{doc.titolo}</p>
              <p className="text-sm text-muted-foreground mt-0.5">{doc.struttura}</p>
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><CalendarDays className="w-3 h-3" />{doc.data}</p>
            </div>
          </div>
          <div className="p-4 rounded-2xl bg-muted/20 border border-border/20">
            <p className="text-base text-foreground leading-relaxed">{doc.desc}</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1 h-12 rounded-2xl text-base" onClick={onClose} data-testid="doc-viewer-close">Chiudi</Button>
            <Button className="flex-1 h-12 rounded-2xl text-base gap-2 bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white" onClick={() => toast.success('Download avviato', { description: `${doc.titolo} — PDF` })} data-testid="doc-viewer-download">
              <Download className="w-5 h-5" />Scarica PDF
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ══════ TAB DOCUMENTI ══════ */
function DocumentsContent() {
  const [viewingDoc, setViewingDoc] = useState(null);
  const [query, setQuery] = useState('');
  const filtered = query.trim() ? documentiPaziente.filter(d => d.titolo.toLowerCase().includes(query.toLowerCase())) : documentiPaziente;

  return (
    <div className="space-y-5" data-testid="documents-content">
      {/* Security Banner */}
      <Card className="border-0 shadow-none rounded-3xl bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/10 border border-emerald-100" data-testid="security-banner">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
          </div>
          <p className="text-sm font-medium text-emerald-800">I tuoi documenti sono sincronizzati in modo sicuro con il tuo Fascicolo Sanitario Elettronico (FSE).</p>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <input className="w-full h-12 pl-12 pr-4 rounded-2xl border border-border/40 bg-card text-base focus:outline-none focus:ring-2 focus:ring-sky-200" placeholder="Cerca documento..." value={query} onChange={(e) => setQuery(e.target.value)} data-testid="doc-search-input" />
      </div>

      {/* List */}
      <div className="space-y-3" data-testid="doc-list">
        {filtered.map((doc, i) => {
          const cfg = tipoConfig[doc.tipo] || tipoConfig.referto;
          const Icon = cfg.icon;
          return (
            <motion.div key={doc.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
              <Card className="border-0 shadow-[0_1px_5px_rgba(0,0,0,0.05)] rounded-3xl hover:shadow-md transition-shadow" data-testid={`doc-${doc.id}`}>
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: cfg.bg }}>
                    <Icon className="w-6 h-6" style={{ color: cfg.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-bold text-foreground">{doc.titolo}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Badge className="text-[9px] border-0 rounded-full" style={{ backgroundColor: `${cfg.color}12`, color: cfg.color }}>{cfg.label}</Badge>
                      <span className="text-xs text-muted-foreground flex items-center gap-1"><CalendarDays className="w-3 h-3" />{doc.data}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => setViewingDoc(doc)} className="w-11 h-11 rounded-2xl flex items-center justify-center bg-sky-50 text-sky-600 hover:bg-sky-100 transition-colors" data-testid={`btn-leggi-${doc.id}`}><Eye className="w-5 h-5" /></button>
                    <button onClick={() => toast.success('Download avviato', { description: doc.titolo })} className="w-11 h-11 rounded-2xl flex items-center justify-center bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors" data-testid={`btn-scarica-${doc.id}`}><Download className="w-5 h-5" /></button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
        {filtered.length === 0 && (
          <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)] rounded-3xl">
            <CardContent className="p-10 flex flex-col items-center text-center" data-testid="doc-empty">
              <FolderOpen className="w-10 h-10 text-muted-foreground/30 mb-3" />
              <p className="text-base font-medium text-muted-foreground">Nessun documento trovato</p>
            </CardContent>
          </Card>
        )}
      </div>

      <AnimatePresence>{viewingDoc && <DocViewer doc={viewingDoc} onClose={() => setViewingDoc(null)} />}</AnimatePresence>
    </div>
  );
}

/* ══════ TAB EDUCAZIONE ══════ */
function EducationContent() {
  return (
    <div className="space-y-8" data-testid="education-content">
      {/* Materiali */}
      <div className="space-y-4" data-testid="materiali-section">
        <h2 className="text-base font-bold text-foreground px-1">Materiali per te</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {materialiEducativi.map((m, i) => {
            const cfg = tipoEduConfig[m.tipo] || tipoEduConfig.guida;
            const Icon = cfg.icon;
            return (
              <motion.div key={m.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
                <Card className="border-0 shadow-[0_1px_6px_rgba(0,0,0,0.05)] rounded-3xl overflow-hidden hover:shadow-md transition-shadow cursor-pointer group" onClick={() => toast.success(m.tipo === 'video' ? 'Riproduzione video...' : 'Apertura guida...', { description: m.titolo })} data-testid={`materiale-${m.id}`}>
                  <CardContent className="p-0">
                    <div className="h-32 flex items-center justify-center relative" style={{ backgroundColor: cfg.bg }}>
                      <div className="w-16 h-16 rounded-full bg-white/80 flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
                        <Icon className="w-8 h-8" style={{ color: cfg.color }} />
                      </div>
                      <Badge className="absolute top-3 right-3 rounded-full text-[9px] border-0" style={{ backgroundColor: `${cfg.color}20`, color: cfg.color }}>
                        {cfg.label}{m.durata && ` · ${m.durata}`}{m.pagine && ` · ${m.pagine} pag.`}
                      </Badge>
                    </div>
                    <div className="p-4">
                      <p className="text-sm font-bold text-foreground leading-snug">{m.titolo}</p>
                      <p className="text-[11px] text-muted-foreground mt-1.5 leading-relaxed">{m.desc}</p>
                      <Badge variant="outline" className="mt-3 rounded-full text-[9px]">{m.categoria}</Badge>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Servizi */}
      <div className="space-y-4" data-testid="servizi-section">
        <h2 className="text-base font-bold text-foreground px-1">Servizi del Territorio</h2>
        <div className="space-y-3">
          {serviziTerritorio.map((s, i) => {
            const Icon = servizioIcons[s.tipo] || Gift;
            const color = servizioColors[s.tipo] || '#6B7280';
            return (
              <motion.div key={s.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + i * 0.05 }}>
                <button className="w-full text-left" onClick={() => toast.success('Apertura servizio...', { description: s.titolo })} data-testid={`servizio-${s.id}`}>
                  <Card className="border-0 shadow-[0_1px_4px_rgba(0,0,0,0.04)] rounded-3xl hover:shadow-md transition-shadow">
                    <CardContent className="p-5 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}12` }}>
                        <Icon className="w-6 h-6" style={{ color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-bold text-foreground">{s.titolo}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{s.desc}</p>
                      </div>
                      <div className="w-9 h-9 rounded-xl bg-muted/20 flex items-center justify-center flex-shrink-0">
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ══════ PAGINA PRINCIPALE ══════ */
export default function PatientArchiveWrapper() {
  const [activeTab, setActiveTab] = useState('documenti');

  return (
    <AppLayout role="paziente">
      <div className="space-y-6 max-w-3xl mx-auto" data-testid="patient-archive-wrapper">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-indigo-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground" data-testid="page-title">Archivio & Educazione</h1>
            <p className="text-sm text-muted-foreground">Documenti clinici, guide e servizi del territorio</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 p-1.5 bg-muted/30 rounded-3xl border border-border/20 w-full sm:w-fit" data-testid="archive-tab-selector">
          {tabs.map((t) => {
            const Icon = t.icon;
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`flex-1 sm:flex-initial flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-2xl text-base font-bold transition-all ${isActive ? 'bg-card text-foreground shadow-md' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid={`tab-${t.id}`}
              >
                <Icon className="w-5 h-5" />{t.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'documenti' ? (
            <motion.div key="documenti" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
              <DocumentsContent />
            </motion.div>
          ) : (
            <motion.div key="educazione" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
              <EducationContent />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AppLayout>
  );
}
